import React, { useEffect, useMemo, useState } from 'react';
import {
  useActiveFixturesSummary,
  useApplyModelMarketPlayers,
  useDeleteModelMarketFixtureSleeperLink,
  useEnsureModelMarketFixture,
  useEnsureModelMarkets,
  useIngestModelMarketObservations,
  useIngestModelMarketObservationsBatch,
  useModelMarketObservations,
  useModelMarketRegistry,
  useModelMarketRules,
  usePreviewModelMarketPlayers,
  usePutModelMarketFixtureSleeperLink,
  useSearchModelMarketPlayers,
} from '../api/hooks';
import {
  MODEL_MARKET_WRITER_TARGETS,
  batchSuspensionFixtureLabel,
  modelMarketWriterTarget,
  normalizeBatchSuspensionFixture,
  sportBucketForFixture,
} from '../utils/batchSuspensionUtils';
import {
  filterFixtures,
  startOfDay,
} from './TradingDash/tradingDashUtils';
import { useHomebaseModal } from '../components/HomebaseModal';
import './BatchSuspensionsPage.css';
import './ModelMarketsPage.css';

const CREATE_VALUE = '__create__';

/** Identity stands alone; single vs batch is the upload's scope, not a different task. */
const MM_TABS = [
  { id: 'identity', label: 'Fixtures & players' },
  { id: 'single', label: 'Single upload' },
  { id: 'batch', label: 'Batch upload' },
];
const DEFAULT_WRITER = modelMarketWriterTarget('wnba');

const OBS_CSV_TEMPLATE =
  'player_id,optic_player_id,line,p_fair,P\nexample_player,OPTICID,32.5,0.55,0.02\n';

/** Batch: fixture_id leads, and two rows on different fixtures so the shape is unmistakable. */
const BATCH_CSV_TEMPLATE =
  'fixture_id,player_id,optic_player_id,line,p_fair,P\n' +
  'FIXTURE_A,example_player,OPTICID,32.5,0.55,0.02\n' +
  'FIXTURE_B,other_player,OPTICID2,27.5,0.48,0.02\n';

const PLAYER_CSV_TEMPLATE =
  'optic_player_id,sleeper_player_id,optic_player_name,sleeper_player_name,optic_player_team,optic_player_team_abbr,optic_player_position,sleeper_player_team,sport,league\n';

/** Columns returned by GET /api/model-markets/players/search (full sleeper_player_sync row). */
const PLAYER_SEARCH_COLUMNS = [
  'optic_player_id',
  'optic_numerical_player_id',
  'optic_player_name',
  'optic_player_team',
  'optic_player_team_abbr',
  'optic_player_position',
  'sleeper_player_id',
  'sportradar_id',
  'opta_id',
  'sleeper_player_name',
  'sleeper_player_team',
  'confidence',
  'sport',
  'league',
  'is_manual',
  'manual_set_at',
  'manual_set_by',
  'sync_timestamp',
];

function formatPlayerSearchCell(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

function formatApiError(err) {
  const d = err?.response?.data;
  if (typeof d?.detail === 'string') return d.detail;
  if (d?.detail && typeof d.detail === 'object' && d.detail.message) {
    return d.detail.message;
  }
  if (Array.isArray(d?.detail)) {
    return d.detail.map((x) => x.msg || JSON.stringify(x)).join('; ');
  }
  return err?.message || 'Request failed';
}

function parseCsv(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const cols = line.split(',').map((c) => c.trim());
    const row = {};
    headers.forEach((h, i) => {
      row[h] = cols[i];
    });
    return row;
  });
}

function playerSlugFromCell(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

function fixtureOptionLabel(f) {
  if (!f?.game_id) return '';
  const matchup = batchSuspensionFixtureLabel(f);
  const league = f.league ? ` · ${f.league}` : '';
  const futures = f.is_futures ? ' · futures' : '';
  const sleeper = f.sleeper_game_id ? ` · sleeper ${f.sleeper_game_id}` : '';
  return `${matchup}${league}${futures}${sleeper} (${f.game_id})`;
}

export default function ModelMarketsPage() {
  const { alert, ModalLayer } = useHomebaseModal();
  const [writerBucket, setWriterBucket] = useState(DEFAULT_WRITER.bucket);
  const writerTarget = useMemo(
    () => modelMarketWriterTarget(writerBucket),
    [writerBucket]
  );
  const writerScope = useMemo(
    () => ({
      sport_bucket: writerTarget.bucket,
      sport: writerTarget.sport,
      league: writerTarget.league,
    }),
    [writerTarget]
  );

  const { data: rulesData, isLoading: rulesLoading, isError: rulesError, error: rulesErr } =
    useModelMarketRules(writerScope);
  const { data: registryData, refetch: refetchRegistry } = useModelMarketRegistry(writerScope);
  const { data: fixturesPayload, isLoading: fixturesLoading, refetch: refetchFixtures } =
    useActiveFixturesSummary();
  const ensureFixtureMut = useEnsureModelMarketFixture();
  const putSleeperLinkMut = usePutModelMarketFixtureSleeperLink();
  const deleteSleeperLinkMut = useDeleteModelMarketFixtureSleeperLink();
  const searchPlayersMut = useSearchModelMarketPlayers();
  const previewPlayersMut = usePreviewModelMarketPlayers();
  const applyPlayersMut = useApplyModelMarketPlayers();
  const ensureMarketsMut = useEnsureModelMarkets();
  const ingestMut = useIngestModelMarketObservations();
  const ingestBatchMut = useIngestModelMarketObservationsBatch();

  const rules = useMemo(
    () => (Array.isArray(rulesData?.rules) ? rulesData.rules : []),
    [rulesData]
  );
  const registry = useMemo(
    () => (Array.isArray(registryData?.models) ? registryData.models : []),
    [registryData]
  );

  const defaultMarketId = rules[0]?.target_market_id || '';

  const [hubFixtures, setHubFixtures] = useState([]);
  const [fixtureSelect, setFixtureSelect] = useState('');
  const [fixtureSearch, setFixtureSearch] = useState('');
  const [fixtureSportFilter, setFixtureSportFilter] = useState(DEFAULT_WRITER.bucket);
  const [fixtureLeagueFilter, setFixtureLeagueFilter] = useState('all');
  const [fixtureTodayOnly, setFixtureTodayOnly] = useState(false);
  const [createSport, setCreateSport] = useState(DEFAULT_WRITER.sport);
  const [createLeague, setCreateLeague] = useState(DEFAULT_WRITER.league);
  const [createHome, setCreateHome] = useState('');
  const [createAway, setCreateAway] = useState('');
  const [createHomeAbbr, setCreateHomeAbbr] = useState('');
  const [createAwayAbbr, setCreateAwayAbbr] = useState('');
  const [createAllowEmptyTeams, setCreateAllowEmptyTeams] = useState(false);
  const [createIsFutures, setCreateIsFutures] = useState(false);
  const [createStart, setCreateStart] = useState('');
  const [createGameId, setCreateGameId] = useState('');
  const [createSleeperGameId, setCreateSleeperGameId] = useState('');
  const [linkSleeperGameId, setLinkSleeperGameId] = useState('');
  const [linkHomeAbbr, setLinkHomeAbbr] = useState('');
  const [linkAwayAbbr, setLinkAwayAbbr] = useState('');
  const [linkLock, setLinkLock] = useState(true);
  const [linkClearOther, setLinkClearOther] = useState(true);

  const [playerSearchQ, setPlayerSearchQ] = useState('');
  const [playerSearchLeague, setPlayerSearchLeague] = useState(DEFAULT_WRITER.league);
  const [playerSearchTeam, setPlayerSearchTeam] = useState('');
  const [playerSearchFeedIds, setPlayerSearchFeedIds] = useState(false);
  const [playerFeedIdIndex, setPlayerFeedIdIndex] = useState(null);
  const [playerSearchHits, setPlayerSearchHits] = useState([]);
  const [playerSearchDone, setPlayerSearchDone] = useState(false);
  const [playerPreview, setPlayerPreview] = useState(null);
  const [allowPlayerOverwrite, setAllowPlayerOverwrite] = useState(false);

  const [sourceMode, setSourceMode] = useState('registry');
  const [modelName, setModelName] = useState('model_v1');
  const [modelVersion, setModelVersion] = useState('');
  const [marketId, setMarketId] = useState('');
  /** Registry only: fixture_id value as stored in model_market_observations (may ≠ step 1). */
  const [modelTableFixtureId, setModelTableFixtureId] = useState('');
  const [csvText, setCsvText] = useState(OBS_CSV_TEMPLATE);
  const [registryModel, setRegistryModel] = useState('');

  const [playersCsvText, setPlayersCsvText] = useState(PLAYER_CSV_TEMPLATE);
  const [linkCompetitors, setLinkCompetitors] = useState(false);
  /** signal = Kalman update; reseed = hard-replace EntriesKF posterior + line */
  const [applyMode, setApplyMode] = useState('signal');

  /** Which tab is showing. Every other piece of state stays page-level, so switching
   *  tabs changes what is visible and never what was typed. */
  const [activeTab, setActiveTab] = useState('identity');
  const [batchResults, setBatchResults] = useState(null);
  /** Fixture ids the writer rejected as unknown; offers a jump back to the identity tab. */
  const [batchUnknownFixtures, setBatchUnknownFixtures] = useState([]);
  const batchMode = activeTab === 'batch';

  const [lastError, setLastError] = useState(null);
  const [lastSuccess, setLastSuccess] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCreateSport(writerTarget.sport);
    setCreateLeague(writerTarget.league);
    setFixtureSportFilter(writerTarget.bucket);
    if (writerTarget.league) setPlayerSearchLeague(writerTarget.league);
    setMarketId('');
    setRegistryModel('');
    setLastError(null);
  }, [writerTarget.bucket, writerTarget.sport, writerTarget.league]);

  useEffect(() => {
    if (!marketId && defaultMarketId) setMarketId(defaultMarketId);
  }, [defaultMarketId, marketId]);

  useEffect(() => {
    if (sourceMode !== 'registry' || registryModel) return;
    if (registry.length) setRegistryModel(registry[0].model_name);
  }, [registry, registryModel, sourceMode]);

  const activeMarketId = String(marketId || defaultMarketId).trim();
  const activeRegistryModel = String(registryModel || '').trim();
  const activeModelTableFixtureId = String(modelTableFixtureId || '').trim();

  const { data: storedObsData, isFetching: storedObsLoading } = useModelMarketObservations(
    sourceMode === 'registry' ? activeRegistryModel : null,
    sourceMode === 'registry' ? activeModelTableFixtureId : null,
    sourceMode === 'registry' ? activeMarketId : null,
    writerScope
  );

  const activeFixtures = useMemo(
    () =>
      (fixturesPayload?.fixtures || [])
        .map(normalizeBatchSuspensionFixture)
        .filter((f) => f?.game_id)
        .sort((a, b) => {
          const ta = a.start_time_utc ? new Date(a.start_time_utc).getTime() : 0;
          const tb = b.start_time_utc ? new Date(b.start_time_utc).getTime() : 0;
          return ta - tb;
        }),
    [fixturesPayload]
  );

  const fixtureOptions = useMemo(() => {
    const byId = new Map();
    // Hub-created first, then active summary last so DB-backed sleeper_game_id wins.
    for (const f of [...hubFixtures, ...activeFixtures]) {
      if (!f?.game_id) continue;
      const prev = byId.get(f.game_id);
      if (!prev) {
        byId.set(f.game_id, f);
        continue;
      }
      byId.set(f.game_id, {
        ...prev,
        ...f,
        sleeper_game_id: f.sleeper_game_id || prev.sleeper_game_id || null,
        is_futures: Boolean(f.is_futures ?? prev.is_futures),
      });
    }
    return [...byId.values()].sort((a, b) =>
      fixtureOptionLabel(a).localeCompare(fixtureOptionLabel(b))
    );
  }, [activeFixtures, hubFixtures]);

  const fixtureSportOptions = useMemo(() => {
    const buckets = new Set();
    for (const f of fixtureOptions) {
      buckets.add(sportBucketForFixture(f));
    }
    return [...buckets].sort();
  }, [fixtureOptions]);

  const fixtureLeagueOptions = useMemo(() => {
    const leagues = new Set();
    for (const f of fixtureOptions) {
      const lg = String(f.league || '').trim();
      if (lg) leagues.add(lg);
    }
    return [...leagues].sort((a, b) => a.localeCompare(b));
  }, [fixtureOptions]);

  const filteredFixtureOptions = useMemo(() => {
    const selectedDate = fixtureTodayOnly ? startOfDay(new Date()) : null;
    let rows = filterFixtures(fixtureOptions, {
      selectedDate,
      sport: fixtureSportFilter,
      league: fixtureLeagueFilter,
      search: '',
      includeInGame: true,
    });
    const q = fixtureSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((f) => {
        const blob = [
          f.home_team,
          f.away_team,
          f.league,
          f.game_id,
          f.sleeper_game_id,
          f.sport,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return blob.includes(q);
      });
    }
    const selectedId = fixtureSelect && fixtureSelect !== CREATE_VALUE ? fixtureSelect : '';
    if (selectedId && !rows.some((f) => f.game_id === selectedId)) {
      const selected = fixtureOptions.find((f) => f.game_id === selectedId);
      if (selected) rows = [selected, ...rows];
    }
    return rows;
  }, [
    fixtureOptions,
    fixtureTodayOnly,
    fixtureSportFilter,
    fixtureLeagueFilter,
    fixtureSearch,
    fixtureSelect,
  ]);

  const creatingNew = fixtureSelect === CREATE_VALUE;
  const selectedFixtureId = creatingNew ? '' : fixtureSelect;
  const selectedFixture = useMemo(
    () => fixtureOptions.find((f) => f.game_id === selectedFixtureId) || null,
    [fixtureOptions, selectedFixtureId]
  );

  useEffect(() => {
    if (!selectedFixture) return;
    setLinkSleeperGameId(String(selectedFixture.sleeper_game_id || ''));
    setLinkHomeAbbr(String(selectedFixture.home_team_abbr || ''));
    setLinkAwayAbbr(String(selectedFixture.away_team_abbr || ''));
    setLinkLock(selectedFixture.sleeper_link_locked !== false);
    const bucket = sportBucketForFixture(selectedFixture);
    if (bucket && MODEL_MARKET_WRITER_TARGETS.some((t) => t.bucket === bucket)) {
      setWriterBucket(bucket);
    }
  }, [selectedFixtureId]); // eslint-disable-line react-hooks/exhaustive-deps

  const csvRows = useMemo(() => parseCsv(csvText), [csvText]);

  /** Batch mode reads fixture_id off each CSV row; step 1's fixture is not used. */
  const batchFixtureIds = useMemo(() => {
    const ids = csvRows.map((r) => String(r.fixture_id || r.game_id || '').trim()).filter(Boolean);
    return [...new Set(ids)];
  }, [csvRows]);
  const batchRowsMissingFixture = useMemo(
    () => csvRows.filter((r) => !String(r.fixture_id || r.game_id || '').trim()).length,
    [csvRows]
  );
  const playersCsvRows = useMemo(() => parseCsv(playersCsvText), [playersCsvText]);
  const storedObsRows = useMemo(
    () => (Array.isArray(storedObsData?.rows) ? storedObsData.rows : []),
    [storedObsData]
  );

  const sourceRows = useMemo(() => {
    if (sourceMode === 'registry') return storedObsRows;
    return csvRows;
  }, [sourceMode, storedObsRows, csvRows]);

  const observationPlayerIds = useMemo(() => {
    const ids = sourceRows
      .map((r) => playerSlugFromCell(r.player_id || r.player_name || r.player_slug))
      .filter(Boolean);
    return [...new Set(ids)];
  }, [sourceRows]);

  const duplicatePlayers = useMemo(() => {
    const counts = new Map();
    for (const r of sourceRows) {
      const pid = playerSlugFromCell(r.player_id || r.player_name || r.player_slug);
      if (!pid) continue;
      counts.set(pid, (counts.get(pid) || 0) + 1);
    }
    return [...counts.entries()].filter(([, n]) => n > 1).map(([pid]) => pid);
  }, [sourceRows]);

  /** Distinct fixture_id values from the model table (or in-memory registry fallback). */
  const modelTableFixtureOptions = useMemo(() => {
    const m = registry.find((x) => x.model_name === activeRegistryModel);
    if (!m) return [];
    const byId = new Map();
    for (const fx of m.fixtures || []) {
      if (!fx?.fixture_id) continue;
      if (fx.market_id && fx.market_id !== activeMarketId) continue;
      byId.set(fx.fixture_id, {
        fixture_id: fx.fixture_id,
        row_count: fx.row_count || 0,
        market_id: fx.market_id || activeMarketId,
      });
    }
    // Older / memory-only registry payloads expose flat fixture_ids without fixtures[].
    if (!byId.size && Array.isArray(m.fixture_ids)) {
      const marketOk =
        !Array.isArray(m.market_ids) ||
        !m.market_ids.length ||
        m.market_ids.includes(activeMarketId);
      if (marketOk) {
        for (const fid of m.fixture_ids) {
          if (!fid) continue;
          byId.set(fid, {
            fixture_id: fid,
            row_count: m.row_count || 0,
            market_id: activeMarketId,
          });
        }
      }
    }
    return [...byId.values()].sort((a, b) => a.fixture_id.localeCompare(b.fixture_id));
  }, [registry, activeRegistryModel, activeMarketId]);

  useEffect(() => {
    if (sourceMode !== 'registry') return;
    if (!activeModelTableFixtureId) return;
    if (!modelTableFixtureOptions.some((f) => f.fixture_id === activeModelTableFixtureId)) {
      setModelTableFixtureId('');
    }
  }, [sourceMode, activeModelTableFixtureId, modelTableFixtureOptions]);

  const parentSummary = useMemo(() => {
    const mid = marketId || defaultMarketId;
    const rule = rules.find((r) => r.target_market_id === mid) || rules[0];
    return rule?.parents || [];
  }, [rules, marketId, defaultMarketId]);

  const marketsPreview = useMemo(() => {
    const fid = String(selectedFixtureId || '').trim();
    const mid = activeMarketId;
    if (!fid || !observationPlayerIds.length) return [];

    const byPlayer = new Map();
    for (const r of sourceRows) {
      const pid = playerSlugFromCell(r.player_id || r.player_name || r.player_slug);
      if (!pid) continue;
      byPlayer.set(pid, {
        line: r.line,
        p_fair: r.p_fair,
        P: r.P || r.r || r.uncertainty || r.R,
        optic_player_id: r.optic_player_id || r.optic_id || null,
      });
    }

    return observationPlayerIds.map((pid) => {
      const obs = byPlayer.get(pid) || {};
      return {
        player_id: pid,
        market_key: `${fid}:${pid}:${mid}`,
        market_id: mid,
        line: obs.line ?? '—',
        p_fair: obs.p_fair ?? '—',
        P: obs.P ?? '—',
        optic_player_id: obs.optic_player_id ?? '—',
        parents: parentSummary.map((p) => ({
          market_key: `${fid}:${pid}:${p.market_id}`,
          market_id: p.market_id,
          roles: p.roles || [],
        })),
      };
    });
  }, [selectedFixtureId, activeMarketId, observationPlayerIds, sourceRows, parentSummary]);

  function rememberHubFixture(row) {
    const fx = normalizeBatchSuspensionFixture({
      game_id: row.game_id || row.fixture_id,
      home_team: row.home_team,
      away_team: row.away_team,
      sport: row.sport,
      league: row.league,
      start_time_utc: row.start_time_utc,
      sleeper_game_id: row.sleeper_game_id,
      is_futures: Boolean(row.is_futures),
    });
    if (!fx?.game_id) return;
    setHubFixtures((prev) => {
      const next = prev.filter((p) => p.game_id !== fx.game_id);
      return [...next, { ...fx, is_futures: Boolean(row.is_futures) }];
    });
    setFixtureSelect(fx.game_id);
  }

  async function onCreateFixture() {
    setLastError(null);
    setLastSuccess(null);
    if (
      !createAllowEmptyTeams &&
      (!String(createHome).trim() || !String(createAway).trim())
    ) {
      await alert({
        title: 'Missing teams',
        body: 'Home and away team names are required (or enable leave teams empty).',
        warn: true,
      });
      return;
    }
    const sleeperId = String(createSleeperGameId || '').trim();
    if (!sleeperId) {
      await alert({
        title: 'Sleeper game id required',
        body: 'sleeper_game_id is required so the fixture can link to Sleeper.',
        warn: true,
      });
      return;
    }
    if (createIsFutures && !String(createStart || '').trim()) {
      await alert({
        title: 'Futures end time required',
        body: 'Set start_time_utc to when season-long ingest should stop (must be in the future).',
        warn: true,
      });
      return;
    }
    setBusy(true);
    try {
      const homeTeam = createAllowEmptyTeams ? String(createHome).trim() : createHome.trim();
      const awayTeam = createAllowEmptyTeams ? String(createAway).trim() : createAway.trim();
      const homeAbbr = createAllowEmptyTeams ? '' : String(createHomeAbbr || '').trim().toUpperCase();
      const awayAbbr = createAllowEmptyTeams ? '' : String(createAwayAbbr || '').trim().toUpperCase();
      const body = {
        minted: true,
        sport: createSport || writerTarget.sport,
        league: createLeague || writerTarget.league,
        sport_bucket: writerTarget.bucket,
        home_team: homeTeam,
        away_team: awayTeam,
        allow_empty_teams: Boolean(createAllowEmptyTeams),
        home_team_abbr: homeAbbr,
        away_team_abbr: awayAbbr,
        start_time_utc: createStart || undefined,
        sleeper_game_id: sleeperId,
        is_futures: Boolean(createIsFutures),
      };
      const customId = String(createGameId || '').trim();
      if (customId) body.game_id = customId;

      const res = await ensureFixtureMut.mutateAsync(body);
      const created = {
        game_id: res.game_id,
        home_team: homeTeam,
        away_team: awayTeam,
        home_team_abbr: homeAbbr,
        away_team_abbr: awayAbbr,
        sport: createSport,
        league: createLeague,
        start_time_utc: createStart || null,
        sleeper_game_id: sleeperId,
        is_futures: Boolean(createIsFutures),
        ...(res.fixture || {}),
      };
      rememberHubFixture(created);
      setCreateSleeperGameId('');
      setLastSuccess(
        `Created & loaded fixture ${res.game_id} (sleeper_game_id=${sleeperId}` +
          `${createIsFutures ? ', futures' : ''})`
      );
      refetchFixtures();
      return res.game_id;
    } catch (e) {
      const msg = formatApiError(e);
      setLastError(msg);
      await alert({ title: 'Create fixture failed', body: msg, warn: true });
    } finally {
      setBusy(false);
    }
  }

  async function onLinkSleeperGameId() {
    setLastError(null);
    setLastSuccess(null);
    setBusy(true);
    try {
      const gid = String(selectedFixtureId || '').trim();
      if (!gid) throw new Error('Select a fixture first');
      const sleeperId = String(linkSleeperGameId || '').trim();
      if (!sleeperId) throw new Error('Enter a sleeper_game_id');
      const sport = selectedFixture?.sport || createSport || writerTarget.sport;
      const league = selectedFixture?.league || createLeague || writerTarget.league;
      const bucket =
        sportBucketForFixture({ sport, league }) || writerTarget.bucket;
      const res = await putSleeperLinkMut.mutateAsync({
        game_id: gid,
        sleeper_game_id: sleeperId,
        home_team_abbr: linkHomeAbbr || undefined,
        away_team_abbr: linkAwayAbbr || undefined,
        clear_other: linkClearOther,
        lock: linkLock,
        sport,
        league,
        sport_bucket: bucket,
      });
      rememberHubFixture({
        ...(selectedFixture || {}),
        game_id: gid,
        sleeper_game_id: sleeperId,
        home_team_abbr: linkHomeAbbr || selectedFixture?.home_team_abbr,
        away_team_abbr: linkAwayAbbr || selectedFixture?.away_team_abbr,
        sleeper_link_locked: linkLock,
        ...(res.fixture || {}),
      });
      setLastSuccess(
        `Linked sleeper_game_id=${sleeperId} on ${gid}` +
          (res.cleared_other ? ` (cleared ${res.cleared_other} other)` : '') +
          (linkLock ? ' · locked' : '')
      );
      refetchFixtures();
    } catch (e) {
      setLastError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function onClearSleeperLink() {
    setLastError(null);
    setLastSuccess(null);
    setBusy(true);
    try {
      const gid = String(selectedFixtureId || '').trim();
      if (!gid) throw new Error('Select a fixture first');
      const sport = selectedFixture?.sport || createSport || writerTarget.sport;
      const league = selectedFixture?.league || createLeague || writerTarget.league;
      const bucket =
        sportBucketForFixture({ sport, league }) || writerTarget.bucket;
      await deleteSleeperLinkMut.mutateAsync({
        game_id: gid,
        sport,
        league,
        sport_bucket: bucket,
      });
      setLinkSleeperGameId('');
      rememberHubFixture({
        ...(selectedFixture || {}),
        game_id: gid,
        sleeper_game_id: null,
        sleeper_link_locked: false,
      });
      setLastSuccess(`Cleared sleeper link on ${gid}`);
      refetchFixtures();
    } catch (e) {
      setLastError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function onUploadPlayersCsv() {
    setLastError(null);
    setLastSuccess(null);
    setBusy(true);
    try {
      if (!playersCsvRows.length) {
        throw new Error(
          'Players CSV needs a header and at least one row (optic_player_id, sleeper_player_id, …)'
        );
      }
      // Do NOT fall back to fixture.sport/league — fixture.sport is Optic coarse
      // (e.g. basketball) while sleeper_player_sync.sport is the league/sync key
      // (e.g. custom / wnba). Fixture fallback swapped contest rows to basketball/custom.
      const players = playersCsvRows.map((r) => ({
        optic_player_id: r.optic_player_id || r.player_id,
        player_id: r.player_id || r.optic_player_id,
        sleeper_player_id: r.sleeper_player_id,
        optic_numerical_player_id: r.optic_numerical_player_id,
        optic_player_name: r.optic_player_name || r.player_name,
        sleeper_player_name: r.sleeper_player_name || r.optic_player_name || r.player_name,
        optic_player_team: r.optic_player_team || r.team,
        optic_player_team_abbr: r.optic_player_team_abbr,
        optic_player_position: r.optic_player_position || r.position,
        sleeper_player_team: r.sleeper_player_team || r.optic_player_team || r.team,
        confidence: r.confidence ? Number(r.confidence) : undefined,
        sport: r.sport || createSport || writerTarget.sport,
        league: r.league || createLeague || writerTarget.league,
      }));
      const missingScope = players.filter((p) => !p.sport || !p.league);
      if (missingScope.length) {
        throw new Error(
          'Each player row needs sport + league (CSV columns or Create form defaults). ' +
            'Do not rely on the selected fixture — fixture.sport is not the sync sport key.'
        );
      }
      const sport = players[0].sport;
      const league = players[0].league;
      const body = {
        players,
        sport,
        league,
        sport_bucket: sportBucketForFixture({ sport, league }) || writerTarget.bucket,
        mark_manual: true,
        allow_overwrite: allowPlayerOverwrite,
      };
      const preview = await previewPlayersMut.mutateAsync(body);
      setPlayerPreview(preview);
      const overwrites = preview?.counts?.overwrite || 0;
      const metaUpdates = preview?.counts?.metadata_update || 0;
      if (overwrites && !allowPlayerOverwrite) {
        setLastError(
          `${overwrites} row(s) would overwrite existing mappings — review preview, check “Allow overwrite”, then Apply again`
        );
        return;
      }
      const res = await applyPlayersMut.mutateAsync(body);
      setLastSuccess(
        `Applied ${res.mappings_upserted ?? 0} manual player mapping(s)` +
          (metaUpdates ? ` (${metaUpdates} sport/league/name update(s))` : '') +
          (res.skipped_count ? `; skipped ${res.skipped_count}` : '') +
          '. Reload that sport’s writer player cache (or wait for DB refresh) before rechecking fixture-payload.'
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.preview) {
        setPlayerPreview(detail.preview);
      }
      setLastError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function onSearchPlayers() {
    setLastError(null);
    try {
      const q = String(playerSearchQ || '').trim();
      const league = String(playerSearchLeague || '').trim();
      const team = String(playerSearchTeam || '').trim();
      if (!q && !league && !team) {
        throw new Error('Enter a name, league, or team');
      }
      // Shared DB lookup — no writer routing. Sync rows use sport≈league tokens.
      const res = await searchPlayersMut.mutateAsync({
        q: q || undefined,
        league: league || undefined,
        team: team || undefined,
        limit: 40,
        include_feed_ids: playerSearchFeedIds || undefined,
      });
      setPlayerSearchHits(Array.isArray(res?.players) ? res.players : []);
      setPlayerFeedIdIndex(res?.feed_id_index || null);
      setPlayerSearchDone(true);
    } catch (e) {
      setPlayerSearchDone(false);
      setLastError(formatApiError(e));
    }
  }

  async function resolveFixtureId() {
    if (creatingNew) {
      throw new Error('Create the fixture first (Create & load), or pick one from the dropdown');
    }
    const gid = String(selectedFixtureId || '').trim();
    if (!gid) throw new Error('Select a fixture first');
    await ensureFixtureMut.mutateAsync({
      game_id: gid,
      attach_only: true,
      ...writerScope,
    });
    return gid;
  }

  async function onSubmitBatch() {
    setBusy(true);
    setLastError(null);
    setLastSuccess(null);
    setBatchResults(null);
    setBatchUnknownFixtures([]);
    try {
      const mid = activeMarketId;
      if (!mid) throw new Error('Pick a model market (rules from selected writer)');
      if (!csvRows.length) throw new Error('CSV needs a header and at least one data row');
      if (batchRowsMissingFixture) {
        throw new Error(
          `${batchRowsMissingFixture} row(s) have no fixture_id. Batch mode needs a ` +
            'fixture_id column on every row.'
        );
      }
      const rows = csvRows.map((r) => ({
        ...r,
        fixture_id: String(r.fixture_id || r.game_id || '').trim(),
        market_id: r.market_id || mid,
        model_name: modelName,
      }));
      const res = await ingestBatchMut.mutateAsync({
        model_name: modelName,
        model_version: modelVersion || undefined,
        market_id: mid,
        link_competitors: linkCompetitors,
        apply_mode: applyMode,
        persist: true,
        ensure_markets: true,
        rows,
        ...writerScope,
      });
      await refetchRegistry();
      setBatchResults(res);
      const line =
        `Applied ${res.applied ?? 0} across ${res.fixtures_ok ?? 0}/${res.fixtures_total ?? 0} ` +
        `fixtures (${applyMode}); stored ${res.persisted ?? 0}`;
      if (res.ok) setLastSuccess(line);
      else setLastError(`${line}. ${(res.fixtures_total ?? 0) - (res.fixtures_ok ?? 0)} fixture(s) failed — see below.`);
    } catch (e) {
      // Tabs remove the implied 1→2→3 order, so a missing fixture has to point home.
      const detail = e?.response?.data?.detail;
      setBatchUnknownFixtures(Array.isArray(detail?.fixture_ids) ? detail.fixture_ids : []);
      setLastError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitAll() {
    if (batchMode) return onSubmitBatch();
    setBusy(true);
    setLastError(null);
    setLastSuccess(null);
    setBatchResults(null);
    try {
      const fixtureId = await resolveFixtureId();

      if (duplicatePlayers.length) {
        throw new Error(
          `Multiple results for the same player: ${duplicatePlayers.join(', ')}. ` +
            'Keep one row per player per fixture/market.'
        );
      }

      if (!observationPlayerIds.length) {
        throw new Error(
          sourceMode === 'registry'
            ? 'No stored rows for this model + model-table fixture + market'
            : 'Observation CSV needs player_id (or player_name); optional optic_player_id attaches for publish'
        );
      }

      const mid = activeMarketId;
      if (!mid) throw new Error('Pick a model market (rules from selected writer)');
      await ensureMarketsMut.mutateAsync({
        fixture_id: fixtureId,
        player_ids: observationPlayerIds,
        market_id: mid,
        link_competitors: linkCompetitors,
        ...writerScope,
      });

      let ingestBody;
      if (sourceMode === 'registry') {
        const name = activeRegistryModel;
        if (!name) throw new Error('Pick a registered model name');
        if (!activeModelTableFixtureId) {
          throw new Error('Pick the model-table fixture id to load from');
        }
        ingestBody = {
          model_name: name,
          fixture_id: fixtureId,
          source_fixture_id: activeModelTableFixtureId,
          use_registry: true,
          market_id: mid,
          link_competitors: linkCompetitors,
          apply_mode: applyMode,
          ...writerScope,
        };
      } else {
        if (!csvRows.length) throw new Error('CSV needs a header and at least one data row');
        const rows = csvRows.map((r) => ({
          ...r,
          fixture_id: fixtureId,
          market_id: r.market_id || mid,
          model_name: modelName,
        }));
        ingestBody = {
          model_name: modelName,
          model_version: modelVersion || undefined,
          fixture_id: fixtureId,
          market_id: mid,
          link_competitors: linkCompetitors,
          apply_mode: applyMode,
          persist: true,
          rows,
          ...writerScope,
        };
      }

      const ingest = await ingestMut.mutateAsync(ingestBody);
      await refetchRegistry();
      const sourceNote =
        sourceMode === 'registry' && activeModelTableFixtureId !== fixtureId
          ? `; from model-table fixture ${activeModelTableFixtureId}`
          : '';
      setLastSuccess(
        `Ingested ${ingest.applied_count ?? 0} on ${fixtureId} (${ingest.apply_mode || applyMode})${sourceNote}` +
          (ingest.persisted != null ? `; stored ${ingest.persisted}` : '') +
          (ingest.failed_count ? `; ${ingest.failed_count} failed` : '')
      );
    } catch (e) {
      setLastError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="batch-susp-page mm-page">
      <header className="batch-susp-header mm-header">
        <h1>Model markets</h1>
        <p className="batch-susp-lede">
          Identity (fixture + players) can stand alone. Model source and ingest are optional and
          separate. Rules / ensure / ingest proxy to the selected sport writer.
        </p>
        <label className="batch-susp-field mm-field mm-writer-select">
          <span>Sport writer</span>
          <select
            value={writerBucket}
            onChange={(e) => setWriterBucket(e.target.value)}
          >
            {MODEL_MARKET_WRITER_TARGETS.map((t) => (
              <option key={t.bucket} value={t.bucket}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
      </header>

      {rulesError && (
        <div className="batch-susp-banner batch-susp-banner-err">
          Failed to load rules from {writerTarget.label} writer (
          <code>routing.{writerTarget.bucket}</code>
          ) — is that writer up? {formatApiError(rulesErr)}
        </div>
      )}
      {lastError && (
        <div className="batch-susp-banner batch-susp-banner-err">{lastError}</div>
      )}
      {lastSuccess && <div className="batch-susp-banner">{lastSuccess}</div>}

      <nav className="mm-tabs" role="tablist" aria-label="Model markets sections">
        {MM_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`mm-tab-${t.id}`}
            aria-selected={activeTab === t.id}
            aria-controls={`mm-panel-${t.id}`}
            className={`mm-tab${activeTab === t.id ? ' is-active' : ''}`}
            onClick={() => {
              setActiveTab(t.id);
              if (t.id === 'batch') setSourceMode('csv');
              // Swap the sample header between modes, never over anything typed.
              if (t.id === 'batch') {
                setCsvText((cur) =>
                  cur.trim() === OBS_CSV_TEMPLATE.trim() ? BATCH_CSV_TEMPLATE : cur
                );
              } else if (t.id === 'single') {
                setCsvText((cur) =>
                  cur.trim() === BATCH_CSV_TEMPLATE.trim() ? OBS_CSV_TEMPLATE : cur
                );
              }
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="mm-stack">
        {activeTab === 'identity' ? (
        <div
          className="mm-group mm-group-identity"
          id="mm-panel-identity"
          role="tabpanel"
          aria-labelledby="mm-tab-identity"
        >
          <div className="mm-group-head">
            <h2 className="mm-group-title">Identity</h2>
            <p className="mm-group-sub">
              Fixture sleeper link and player mappings — usable without a model.
            </p>
          </div>
        <section className="batch-susp-card mm-card">
          <h2>1 · Fixture</h2>
          <div className="mm-fixture-filters">
            <div className="mm-fixture-filter-top">
              <label className="batch-susp-field mm-field mm-fixture-search">
                <span>Search</span>
                <input
                  type="search"
                  value={fixtureSearch}
                  onChange={(e) => setFixtureSearch(e.target.value)}
                  placeholder="Team, game id, sleeper id…"
                />
              </label>
              <label className="batch-susp-field mm-field mm-fixture-filter-compact">
                <span>Sport</span>
                <select
                  value={fixtureSportFilter}
                  onChange={(e) => setFixtureSportFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  {fixtureSportOptions.map((bucket) => (
                    <option key={bucket} value={bucket}>
                      {bucket}
                    </option>
                  ))}
                </select>
              </label>
              <label className="batch-susp-field mm-field mm-fixture-filter-compact">
                <span>League</span>
                <select
                  value={fixtureLeagueFilter}
                  onChange={(e) => setFixtureLeagueFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  {fixtureLeagueOptions.map((lg) => (
                    <option key={lg} value={lg}>
                      {lg}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="mm-fixture-filter-meta">
              <label className="mm-fixture-today">
                <input
                  type="checkbox"
                  checked={fixtureTodayOnly}
                  onChange={(e) => setFixtureTodayOnly(e.target.checked)}
                />
                Today only
              </label>
              <span className="batch-susp-field-hint mm-fixture-count">
                {filteredFixtureOptions.length} of {fixtureOptions.length} fixtures
              </span>
              {(fixtureSearch ||
                fixtureSportFilter !== 'all' ||
                fixtureLeagueFilter !== 'all' ||
                fixtureTodayOnly) && (
                <button
                  type="button"
                  className="button mm-fixture-clear"
                  onClick={() => {
                    setFixtureSearch('');
                    setFixtureSportFilter('all');
                    setFixtureLeagueFilter('all');
                    setFixtureTodayOnly(false);
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
          <label className="batch-susp-field mm-field">
            <span>Fixture</span>
            <select
              value={fixtureSelect}
              disabled={fixturesLoading}
              onChange={(e) => setFixtureSelect(e.target.value)}
            >
              <option value="">
                {fixturesLoading ? 'Loading fixtures…' : 'Select fixture…'}
              </option>
              <option value={CREATE_VALUE}>＋ Create new fixture…</option>
              {filteredFixtureOptions.map((f) => (
                <option key={f.game_id} value={f.game_id}>
                  {fixtureOptionLabel(f)}
                </option>
              ))}
            </select>
          </label>
          {!filteredFixtureOptions.length && fixtureOptions.length ? (
            <p className="batch-susp-field-hint">No fixtures match these filters.</p>
          ) : null}

          {creatingNew ? (
            <div className="mm-create">
              <div className="mm-row">
                <label className="batch-susp-field mm-field">
                  <span>Home</span>
                  <input
                    value={createHome}
                    onChange={(e) => setCreateHome(e.target.value)}
                    placeholder={createAllowEmptyTeams ? 'optional (empty OK)' : 'Home team'}
                    disabled={createAllowEmptyTeams}
                  />
                </label>
                <label className="batch-susp-field mm-field">
                  <span>Away</span>
                  <input
                    value={createAway}
                    onChange={(e) => setCreateAway(e.target.value)}
                    placeholder={createAllowEmptyTeams ? 'optional (empty OK)' : 'Away team'}
                    disabled={createAllowEmptyTeams}
                  />
                </label>
              </div>
              <div className="mm-row">
                <label className="batch-susp-field mm-field">
                  <span>Home abbr</span>
                  <input
                    value={createAllowEmptyTeams ? '' : createHomeAbbr}
                    onChange={(e) => setCreateHomeAbbr(e.target.value)}
                    placeholder={createAllowEmptyTeams ? 'auto empty' : 'e.g. DAL'}
                    disabled={createAllowEmptyTeams}
                  />
                </label>
                <label className="batch-susp-field mm-field">
                  <span>Away abbr</span>
                  <input
                    value={createAllowEmptyTeams ? '' : createAwayAbbr}
                    onChange={(e) => setCreateAwayAbbr(e.target.value)}
                    placeholder={createAllowEmptyTeams ? 'auto empty' : 'e.g. NYL'}
                    disabled={createAllowEmptyTeams}
                  />
                </label>
              </div>
              <label className="batch-susp-check">
                <input
                  type="checkbox"
                  checked={createAllowEmptyTeams}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setCreateAllowEmptyTeams(on);
                    if (on) {
                      setCreateHome('');
                      setCreateAway('');
                      setCreateHomeAbbr('');
                      setCreateAwayAbbr('');
                    }
                  }}
                />
                <span>
                  Leave home / away empty (names + abbrs stored as <code>&quot;&quot;</code> —
                  publish-ready, no link step)
                </span>
              </label>
              <label className="batch-susp-check">
                <input
                  type="checkbox"
                  checked={createIsFutures}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setCreateIsFutures(on);
                    if (on) {
                      setCreateAllowEmptyTeams(true);
                      setCreateHome('');
                      setCreateAway('');
                      setCreateHomeAbbr('');
                      setCreateAwayAbbr('');
                    }
                  }}
                />
                <span>
                  Season-long futures shell (<code>is_futures</code>) — writer attaches Optic
                  futures ingest; <code>start_time_utc</code> is when ingest stops
                </span>
              </label>
              <div className="mm-row">
                <label className="batch-susp-field mm-field">
                  <span>Sport</span>
                  <input value={createSport} onChange={(e) => setCreateSport(e.target.value)} />
                </label>
                <label className="batch-susp-field mm-field">
                  <span>League</span>
                  <input value={createLeague} onChange={(e) => setCreateLeague(e.target.value)} />
                </label>
              </div>
              <div className="mm-row">
                <label className="batch-susp-field mm-field">
                  <span>Sleeper game id</span>
                  <input
                    value={createSleeperGameId}
                    onChange={(e) => setCreateSleeperGameId(e.target.value)}
                    placeholder="required"
                  />
                </label>
                <label className="batch-susp-field mm-field">
                  <span>
                    {createIsFutures
                      ? 'End / close time (ISO UTC)'
                      : 'Start (ISO UTC, optional)'}
                  </span>
                  <input
                    value={createStart}
                    onChange={(e) => setCreateStart(e.target.value)}
                    placeholder={
                      createIsFutures ? '2027-02-01T00:00:00Z (required for futures)' : '2026-07-14T23:00:00Z'
                    }
                  />
                </label>
              </div>
              <label className="batch-susp-field mm-field">
                <span>Custom game_id (optional)</span>
                <input
                  value={createGameId}
                  onChange={(e) => setCreateGameId(e.target.value)}
                  placeholder="auto: model_…"
                />
              </label>
              <button
                type="button"
                className="button button-primary"
                disabled={busy}
                onClick={() => onCreateFixture()}
              >
                {busy ? 'Creating…' : 'Create & load fixture'}
              </button>
              <p className="batch-susp-field-hint">
                Writes names, abbrs, and <code>sleeper_game_id</code> to{' '}
                <code>true_odds.fixtures</code> in one step (link panel is only for edits later).
              </p>
            </div>
          ) : selectedFixtureId ? (
            <div className="mm-create">
              <p className="batch-susp-field-hint mm-selected">
                Selected: {selectedFixtureId}
                {selectedFixture?.sleeper_game_id
                  ? ` · sleeper ${selectedFixture.sleeper_game_id}`
                  : ' · no sleeper_game_id yet'}
                {selectedFixture?.sleeper_link_locked ? ' · locked' : ''}
              </p>
              <div className="mm-link-row">
                <label className="batch-susp-field mm-field mm-link-game-id">
                  <span>Sleeper game id</span>
                  <input
                    value={linkSleeperGameId}
                    onChange={(e) => setLinkSleeperGameId(e.target.value)}
                    placeholder="sleeper game id"
                  />
                </label>
                <label className="batch-susp-field mm-field mm-link-abbr">
                  <span>Home abbr</span>
                  <input
                    value={linkHomeAbbr}
                    onChange={(e) => setLinkHomeAbbr(e.target.value)}
                    placeholder="DAL"
                  />
                </label>
                <label className="batch-susp-field mm-field mm-link-abbr">
                  <span>Away abbr</span>
                  <input
                    value={linkAwayAbbr}
                    onChange={(e) => setLinkAwayAbbr(e.target.value)}
                    placeholder="NY"
                  />
                </label>
              </div>
              <div className="mm-row" style={{ alignItems: 'center', gap: '1rem' }}>
                <label className="mm-fixture-today">
                  <input
                    type="checkbox"
                    checked={linkClearOther}
                    onChange={(e) => setLinkClearOther(e.target.checked)}
                  />
                  Clear this sleeper id from other fixtures
                </label>
                <label className="mm-fixture-today">
                  <input
                    type="checkbox"
                    checked={linkLock}
                    onChange={(e) => setLinkLock(e.target.checked)}
                  />
                  Lock link (fixture sync will not overwrite)
                </label>
              </div>
              <div className="mm-actions-inline">
                <button
                  type="button"
                  className="button button-primary mm-btn-compact"
                  disabled={busy}
                  onClick={() => onLinkSleeperGameId()}
                >
                  Save link
                </button>
                <button
                  type="button"
                  className="button mm-btn-compact"
                  disabled={busy || !selectedFixture?.sleeper_game_id}
                  onClick={() => onClearSleeperLink()}
                >
                  Clear link
                </button>
              </div>
              <p className="batch-susp-field-hint">
                Publisher needs sleeper id + team abbrs. Lock keeps auto fixture-sync from changing
                this Optic row (sticky still prefers it for twins).
              </p>
            </div>
          ) : null}
        </section>

        <section className="batch-susp-card mm-card">
          <h2>2 · Players</h2>
          <p className="batch-susp-field-hint">
            Confirm optic↔sleeper rows in <code>sleeper_player_sync</code>, then upload only what
            you need to add or correct. Manual uploads are preserved across BQ sync.
          </p>

          <div className="mm-subblock">
            <h3 className="mm-subhead">Look up</h3>
            <div className="mm-search-filters">
              <label className="batch-susp-field mm-field mm-search-q">
                <span>Name / id</span>
                <input
                  type="search"
                  value={playerSearchQ}
                  onChange={(e) => setPlayerSearchQ(e.target.value)}
                  placeholder="Ionescu…"
                  aria-label="Search players by name or id"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSearchPlayers();
                  }}
                />
              </label>
              <label className="batch-susp-field mm-field mm-search-league">
                <span>League</span>
                <input
                  value={playerSearchLeague}
                  onChange={(e) => setPlayerSearchLeague(e.target.value)}
                  placeholder="wnba"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSearchPlayers();
                  }}
                />
              </label>
              <label className="batch-susp-field mm-field mm-search-team">
                <span>Team</span>
                <input
                  value={playerSearchTeam}
                  onChange={(e) => setPlayerSearchTeam(e.target.value)}
                  placeholder="NYL / Liberty…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSearchPlayers();
                  }}
                />
              </label>
              <button
                type="button"
                className="button mm-btn-compact mm-search-go"
                disabled={busy || searchPlayersMut.isPending}
                onClick={() => onSearchPlayers()}
              >
                {searchPlayersMut.isPending ? '…' : 'Search'}
              </button>
            </div>
            <p className="batch-susp-field-hint">
              Any field works alone or combined (AND). Team matches name or abbr.
            </p>
            <label className="batch-susp-field-hint" style={{ display: 'block', marginTop: '0.4rem' }}>
              <input
                type="checkbox"
                checked={playerSearchFeedIds}
                onChange={(e) => setPlayerSearchFeedIds(e.target.checked)}
                style={{ marginRight: '0.4rem' }}
              />
              Resolve live-feed ids (sportradar_id / opta_id) — first search builds the index and is slow
            </label>
            {playerSearchHits.length ? (
              <div className="mm-search-results">
                <p className="batch-susp-field-hint mm-search-count">
                  {playerSearchHits.length} row{playerSearchHits.length === 1 ? '' : 's'}
                  {playerFeedIdIndex ? (
                    <span style={{ marginLeft: '0.6rem' }}>
                      · feed id index {JSON.stringify(playerFeedIdIndex.indexed)} from{' '}
                      <code>{playerFeedIdIndex.base_url}</code>
                    </span>
                  ) : null}
                </p>
                <div className="mm-table-scroll">
                  <table className="mm-preview-table mm-player-search-table">
                    <thead>
                      <tr>
                        {PLAYER_SEARCH_COLUMNS.map((col) => (
                          <th key={col}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {playerSearchHits.map((p) => (
                        <tr key={`${p.optic_player_id}-${p.sleeper_player_id}-${p.league}`}>
                          {PLAYER_SEARCH_COLUMNS.map((col) => (
                            <td key={col} className="mm-mono">
                              {formatPlayerSearchCell(p[col])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : playerSearchDone ? (
              <p className="batch-susp-field-hint">No matches for that query.</p>
            ) : null}
          </div>

          <div className="mm-subblock">
            <h3 className="mm-subhead">Upload mapping CSV</h3>
            <p className="batch-susp-field-hint">
              Required: <code>optic_player_id</code>, <code>sleeper_player_id</code>,{' '}
              <code>sport</code>, <code>league</code> (or Create-form defaults — not the selected
              fixture). For All-Star / contest: <code>custom</code> /{' '}
              <code>usa_-_wnba_all_star</code>. Overwrites need the checkbox below.
            </p>
            <label className="batch-susp-field mm-field">
              <span>CSV</span>
              <textarea
                rows={4}
                value={playersCsvText}
                onChange={(e) => setPlayersCsvText(e.target.value)}
                className="mm-mono"
                placeholder={PLAYER_CSV_TEMPLATE.trim()}
              />
            </label>
            <div className="mm-upload-actions">
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const reader = new FileReader();
                  reader.onload = () => setPlayersCsvText(String(reader.result || ''));
                  reader.readAsText(f);
                }}
              />
              <label className="mm-fixture-today">
                <input
                  type="checkbox"
                  checked={allowPlayerOverwrite}
                  onChange={(e) => setAllowPlayerOverwrite(e.target.checked)}
                />
                Allow overwrite
              </label>
              <button
                type="button"
                className="button button-primary mm-btn-compact"
                disabled={busy}
                onClick={() => onUploadPlayersCsv()}
              >
                {busy ? 'Applying…' : 'Preview & apply'}
              </button>
            </div>
            {playerPreview?.counts ? (
              <p className="batch-susp-field-hint">
                Preview: new={playerPreview.counts.new || 0}, metadata_update=
                {playerPreview.counts.metadata_update || 0}, unchanged=
                {playerPreview.counts.unchanged || 0}, overwrite=
                {playerPreview.counts.overwrite || 0}, sleeper_collision=
                {playerPreview.counts.sleeper_collision || 0}, invalid=
                {playerPreview.counts.invalid || 0}
                {playerPreview.counts.metadata_update
                  ? ' — metadata_update means sport/league will be rewritten (optic→sleeper unchanged).'
                  : ''}
              </p>
            ) : null}
          </div>
        </section>
        </div>
        ) : null}

        {activeTab !== 'identity' ? (
        <div
          className="mm-group mm-group-model"
          id={`mm-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`mm-tab-${activeTab}`}
        >
          <div className="mm-group-head">
            <h2 className="mm-group-title">
              {batchMode ? 'Batch upload' : 'Single upload'}
            </h2>
            <p className="mm-group-sub">
              {batchMode
                ? 'One CSV across many fixtures. Every row carries its own fixture_id; engines are created per fixture on the pod that owns it.'
                : 'Applies model observations into EntriesKF for the fixture picked in Fixtures & players.'}
            </p>
          </div>
        {!batchMode ? (
          <div className="mm-target-bar">
            {selectedFixtureId ? (
              <>
                <span className="mm-target-label">Target fixture</span>
                <span className="mm-mono">{selectedFixtureId}</span>
                {selectedFixture ? (
                  <span className="mm-target-sub">
                    {batchSuspensionFixtureLabel(selectedFixture)}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="mm-target-warn">
                No fixture selected — pick one in Fixtures &amp; players.
              </span>
            )}
            <button
              type="button"
              className="button mm-btn-compact"
              onClick={() => setActiveTab('identity')}
            >
              {selectedFixtureId ? 'Change' : 'Pick a fixture'}
            </button>
          </div>
        ) : null}
        <section className="batch-susp-card mm-card">
          <h2>3 · Source</h2>
          <div className="mm-row">
            {!batchMode ? (
              <label className="batch-susp-field mm-field">
                <span>Mode</span>
                <select value={sourceMode} onChange={(e) => setSourceMode(e.target.value)}>
                  <option value="registry">Registered model (table)</option>
                  <option value="csv">CSV → store + apply</option>
                </select>
              </label>
            ) : null}
            <label className="batch-susp-field mm-field">
              <span>Market id</span>
              <select
                value={marketId || defaultMarketId}
                onChange={(e) => setMarketId(e.target.value)}
              >
                {(rules.length
                  ? rules.map((r) => r.target_market_id)
                  : [defaultMarketId]
                ).map((mid) => (
                  <option key={mid} value={mid}>
                    {mid}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {batchMode || sourceMode === 'csv' ? (
            <>
              <p className="batch-susp-field-hint">
                {batchMode
                  ? 'CSV needs a fixture_id column. Registry load is single-fixture only.'
                  : 'Uses the selected fixture for the model table and for market init.'}
              </p>
              <div className="mm-row">
                <label className="batch-susp-field mm-field">
                  <span>Model name</span>
                  <input
                    value={modelName}
                    onChange={(e) => setModelName(e.target.value)}
                    placeholder="minutes_v1"
                  />
                </label>
                <label className="batch-susp-field mm-field">
                  <span>Version</span>
                  <input
                    value={modelVersion}
                    onChange={(e) => setModelVersion(e.target.value)}
                    placeholder="optional"
                  />
                </label>
              </div>
              <label className="batch-susp-field mm-field">
                <span>
                  {batchMode
                    ? 'CSV (fixture_id, player_id, line, p_fair, P; optional optic_player_id)'
                    : 'CSV (player_id, line, p_fair, P; optional optic_player_id) — fixture filled from step 1'}
                </span>
                <textarea
                  rows={4}
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                  className="mm-mono"
                  placeholder={(batchMode ? BATCH_CSV_TEMPLATE : OBS_CSV_TEMPLATE).trim()}
                />
              </label>
              <div className="mm-upload-actions">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      setCsvText(String(reader.result || ''));
                      setSourceMode('csv');
                    };
                    reader.readAsText(f);
                  }}
                />
                <button
                  type="button"
                  className="button mm-btn-compact"
                  onClick={() => setCsvText(batchMode ? BATCH_CSV_TEMPLATE : OBS_CSV_TEMPLATE)}
                >
                  Reset to sample
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="batch-susp-field-hint">
                Model-table <code>fixture_id</code> may not match Risk Home. Pick the table’s
                fixture below, then map it onto step‑1 when applying.
              </p>
              <label className="batch-susp-field mm-field">
                <span>Registered model</span>
                <select
                  value={registryModel}
                  onChange={(e) => {
                    setRegistryModel(e.target.value);
                    setModelTableFixtureId('');
                  }}
                >
                  <option value="">Select model…</option>
                  {registry.map((m) => (
                    <option key={m.model_name} value={m.model_name}>
                      {m.model_name} ({m.row_count} rows)
                    </option>
                  ))}
                </select>
              </label>
              <label className="batch-susp-field mm-field">
                <span>Model-table fixture id</span>
                <select
                  value={modelTableFixtureId}
                  onChange={(e) => setModelTableFixtureId(e.target.value)}
                  disabled={!activeRegistryModel}
                >
                  <option value="">
                    {!activeRegistryModel
                      ? 'Pick a model first…'
                      : modelTableFixtureOptions.length
                        ? 'Select fixture_id from model table…'
                        : 'No fixtures stored for this model/market'}
                  </option>
                  {modelTableFixtureOptions.map((f) => (
                    <option key={f.fixture_id} value={f.fixture_id}>
                      {f.fixture_id} ({f.row_count} rows)
                    </option>
                  ))}
                </select>
              </label>
              {selectedFixtureId &&
              activeModelTableFixtureId &&
              activeModelTableFixtureId !== selectedFixtureId ? (
                <p className="batch-susp-field-hint">
                  Map <code>{activeModelTableFixtureId}</code> → apply on step‑1{' '}
                  <code>{selectedFixtureId}</code>
                </p>
              ) : null}
              <p className="batch-susp-field-hint">
                {storedObsLoading
                  ? 'Loading stored rows…'
                  : activeModelTableFixtureId && activeRegistryModel
                    ? `${storedObsRows.length} row(s) from true_odds.model_market_observations`
                    : 'Pick model + model-table fixture'}
              </p>
            </>
          )}
          {duplicatePlayers.length ? (
            <p className="batch-susp-banner batch-susp-banner-err" style={{ marginTop: '0.5rem' }}>
              Duplicate players in source: {duplicatePlayers.join(', ')}
            </p>
          ) : null}
        </section>

        {!batchMode ? (
        <section className="batch-susp-card mm-card">
          <h2>4 · Markets preview</h2>
          <p className="batch-susp-field-hint">
            Market keys use step‑1 fixture. Registry mode loads rows by the model-table fixture id.
          </p>
          {!selectedFixtureId ? (
            <p className="batch-susp-field-hint mm-selected">
              Select a target fixture in step 1.
            </p>
          ) : !observationPlayerIds.length ? (
            <p className="batch-susp-field-hint mm-selected">
              {sourceMode === 'registry'
                ? !activeRegistryModel
                  ? 'Step 1 fixture is set — pick a registered model in step 3 (or switch Source to CSV).'
                  : !activeModelTableFixtureId
                    ? 'Step 1 fixture is set — pick a model-table fixture id in step 3 to load observation rows.'
                    : storedObsLoading
                      ? 'Loading observation rows…'
                      : 'No observation rows for that model + model-table fixture + market (or rows lack player_id). Switch to CSV to paste rows.'
                : 'Step 1 fixture is set — CSV needs at least one data row with player_id (or player_name).'}
            </p>
          ) : !marketsPreview.length ? (
            <p className="batch-susp-field-hint mm-selected">
              Waiting for market id from writer rules…
            </p>
          ) : (
            <div className="mm-preview-wrap">
              <table className="mm-preview-table">
                <thead>
                  <tr>
                    <th>Target market_key</th>
                    <th>optic_player_id</th>
                    <th>line</th>
                    <th>p_fair</th>
                    <th>P</th>
                  </tr>
                </thead>
                <tbody>
                  {marketsPreview.map((row) => (
                    <tr key={row.market_key}>
                      <td className="mm-mono">{row.market_key}</td>
                      <td className="mm-mono">{row.optic_player_id}</td>
                      <td>{row.line}</td>
                      <td>{row.p_fair}</td>
                      <td>{row.P}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parentSummary.length ? (
                <details className="mm-preview-parents">
                  <summary>
                    Parent watch keys ({marketsPreview.length * parentSummary.length})
                  </summary>
                  <ul className="mm-preview-parent-list">
                    {marketsPreview.flatMap((row) =>
                      row.parents.map((p) => (
                        <li key={`${row.player_id}:${p.market_key}`}>
                          <code>{p.market_key}</code>
                          <span className="batch-susp-field-hint">
                            {' '}
                            [{(p.roles || []).join(', ') || '—'}]
                          </span>
                        </li>
                      ))
                    )}
                  </ul>
                </details>
              ) : null}
              <p className="batch-susp-field-hint">
                {marketsPreview.length} target engine
                {marketsPreview.length === 1 ? '' : 's'} · competitors{' '}
                {linkCompetitors ? 'unmuted' : 'muted'} · ingest {applyMode}
              </p>
            </div>
          )}
        </section>
        ) : (
        <section className="batch-susp-card mm-card">
          <h2>4 · Batch summary</h2>
          <div className="mm-batch-summary">
            <div>
              <span className="mm-batch-stat">{csvRows.length}</span>
              <span className="mm-batch-stat-label">rows</span>
            </div>
            <div>
              <span className="mm-batch-stat">{batchFixtureIds.length}</span>
              <span className="mm-batch-stat-label">fixtures</span>
            </div>
            <div>
              <span className="mm-batch-stat">{observationPlayerIds.length}</span>
              <span className="mm-batch-stat-label">players</span>
            </div>
            <div>
              <span className="mm-batch-stat mm-batch-stat-text">{activeMarketId || '—'}</span>
              <span className="mm-batch-stat-label">market</span>
            </div>
          </div>
          {batchRowsMissingFixture ? (
            <p className="batch-susp-field-hint mm-batch-warn">
              {batchRowsMissingFixture} row(s) have no fixture_id — add a fixture_id column.
            </p>
          ) : null}
          {batchUnknownFixtures.length ? (
            <div className="mm-batch-jump">
              <p className="batch-susp-field-hint">
                {batchUnknownFixtures.length} fixture(s) are not in true_odds.fixtures yet:{' '}
                <code className="mm-mono">{batchUnknownFixtures.join(', ')}</code>
              </p>
              <button
                type="button"
                className="button mm-btn-compact"
                onClick={() => setActiveTab('identity')}
              >
                Create them in Fixtures &amp; players
              </button>
            </div>
          ) : null}
          {batchFixtureIds.length ? (
            <details className="mm-batch-fixtures">
              <summary>{batchFixtureIds.length} fixture id(s)</summary>
              <ul>
                {batchFixtureIds.map((fid) => (
                  <li key={fid} className="mm-mono">
                    {fid}
                  </li>
                ))}
              </ul>
            </details>
          ) : (
            <p className="batch-susp-field-hint mm-selected">
              Paste or upload a CSV with a fixture_id column.
            </p>
          )}
        </section>
        )}

        <section className="batch-susp-card mm-card">
          <h2>5 · Competitors</h2>
          <p className="batch-susp-field-hint mm-comp-lede">
            Model engines always run EntriesKF, Order Book, and Entry → OB_to_P. Optic book signals
            stay muted unless you turn them on below.
          </p>
          <label className={`mm-comp-toggle${linkCompetitors ? ' is-on' : ''}`}>
            <input
              type="checkbox"
              checked={linkCompetitors}
              onChange={(e) => setLinkCompetitors(e.target.checked)}
            />
            <span className="mm-comp-toggle-ui" aria-hidden="true" />
            <span className="mm-comp-toggle-copy">
              <span className="mm-comp-toggle-title">
                {linkCompetitors ? 'Competitor signals on' : 'Competitor signals off'}
              </span>
              <span className="mm-comp-toggle-sub">
                {linkCompetitors
                  ? 'SSE books can update this market when they exist'
                  : 'Ignore sportsbook prints — model / entry path only'}
              </span>
            </span>
          </label>
          <fieldset className="mm-apply-mode">
            <legend className="mm-parents-foot-label">Ingest mode</legend>
            <label className="mm-apply-mode-opt">
              <input
                type="radio"
                name="apply_mode"
                value="signal"
                checked={applyMode === 'signal'}
                onChange={() => setApplyMode('signal')}
              />
              <span>
                <strong>Signal</strong> — Kalman-update into existing EntriesKF
              </span>
            </label>
            <label className="mm-apply-mode-opt">
              <input
                type="radio"
                name="apply_mode"
                value="reseed"
                checked={applyMode === 'reseed'}
                onChange={() => setApplyMode('reseed')}
              />
              <span>
                <strong>Reseed</strong> — replace posterior + line from the model row
              </span>
            </label>
          </fieldset>
          <div className="mm-parents-foot">
            <span className="mm-parents-foot-label">Parents</span>
            <span className="mm-parents-foot-body">
              {rulesLoading
                ? 'Loading…'
                : parentSummary.length
                  ? parentSummary
                      .map((p) => `${p.market_id} (${(p.roles || []).join(', ')})`)
                      .join(' · ')
                  : 'None configured — close/rerun won’t track Optic parents'}
            </span>
          </div>
        </section>

        <div className="mm-actions">
          <button
            type="button"
            className="button button-primary"
            disabled={busy || (!batchMode && (creatingNew || !selectedFixtureId))}
            onClick={onSubmitAll}
          >
            {busy
              ? 'Working…'
              : batchMode
                ? `Ensure engines + ingest (${batchFixtureIds.length} fixtures)`
                : 'Ensure engines + ingest'}
          </button>
          {batchMode ? (
            <span className="batch-susp-field-hint">
              {csvRows.length} row(s) across {batchFixtureIds.length} fixture(s)
              {batchRowsMissingFixture ? ` · ${batchRowsMissingFixture} missing fixture_id` : ''}
            </span>
          ) : creatingNew ? (
            <span className="batch-susp-field-hint">Create & load the fixture before ingest</span>
          ) : null}
        </div>
        </div>
        ) : null}

        {batchResults?.results?.length ? (
          <section className="batch-susp-section mm-batch-results">
            <h2>Batch results</h2>
            <p className="batch-susp-field-hint">
              {batchResults.fixtures_ok}/{batchResults.fixtures_total} fixtures applied ·{' '}
              {batchResults.applied} observations · {batchResults.persisted} stored
              {batchResults.failed ? ` · ${batchResults.failed} row(s) failed` : ''}
            </p>
            <div className="mm-table-wrap">
              <table className="batch-susp-table">
                <thead>
                  <tr>
                    <th>Fixture</th>
                    <th>Shard</th>
                    <th>Rows</th>
                    <th>Engines</th>
                    <th>Applied</th>
                    <th>Failed</th>
                    <th>Stored</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {batchResults.results.map((r) => (
                    <tr key={r.fixture_id} className={r.ok ? '' : 'mm-batch-row-failed'}>
                      <td className="mm-mono">{r.fixture_id}</td>
                      <td>{r.shard ?? '—'}</td>
                      <td>{r.rows ?? 0}</td>
                      <td>{r.engines_created ?? 0}</td>
                      <td>{r.applied ?? 0}</td>
                      <td>{(r.failed || []).length}</td>
                      <td>{r.persisted ?? 0}</td>
                      <td className="mm-batch-err">
                        {r.error ? String(r.error) : r.ok ? '' : 'see failed rows'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
      </div>
      <ModalLayer />
    </div>
  );
}
